<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveAdminInventoryStore } from '../../lib/convexRealtime';
  import {
    addAdminTicketExtra,
    fetchAdminEventTickets,
    releaseConvexReservation,
    sweepExpiredReservations,
    updateAdminExtraCapacity,
    updateAdminTicketExtraQuantity
  } from '../../lib/convexApi';
  import type { AdminEventTicketSummary, AdminInventorySkuRecord } from '../../lib/types';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let eventId: string;
  export let eventName: string;

  let store: ReturnType<typeof liveAdminInventoryStore> | null = null;
  let unsubscribe: (() => void) | null = null;
  let subscribedId: string | null = null;

  let records: AdminInventorySkuRecord[] = [];
  let loading = true;
  let error: string | null = null;

  let actionError: string | null = null;
  let notice: string | null = null;
  let busy = false;

  let openSku: string | null = null;
  let capacityDraft = '';
  let quantityDrafts: Record<string, string> = {};
  let addTicketId = '';
  let addQuantity = '1';
  let eventTickets: AdminEventTicketSummary[] = [];

  $: if (eventId && eventId !== subscribedId) {
    subscribedId = eventId;
    openSku = null;
    notice = null;
    actionError = null;
    unsubscribe?.();
    store?.dispose();
    store = liveAdminInventoryStore(eventId);
    unsubscribe = store.subscribe(state => {
      records = state.data ?? [];
      loading = state.loading;
      error = state.error;
    });
  }

  onDestroy(() => {
    unsubscribe?.();
    store?.dispose();
  });

  $: expiredHoldCount = records.reduce(
    (count, record) =>
      count + record.holds.filter(hold => hold.expiresAt != null && hold.expiresAt <= Date.now()).length,
    0
  );

  function isCapacitySku(sku: string): boolean {
    return sku.startsWith('tier:') || sku === 'event:capacity';
  }

  $: extraRecords = records.filter(record => !isCapacitySku(record.sku));
  $: tierRecords = records.filter(record => isCapacitySku(record.sku));

  async function toggleSku(record: AdminInventorySkuRecord) {
    if (openSku === record.sku) {
      openSku = null;
      return;
    }
    openSku = record.sku;
    capacityDraft = record.capacity === -1 ? '' : String(record.capacity);
    quantityDrafts = {};
    addTicketId = '';
    addQuantity = '1';
    if (eventTickets.length === 0) {
      eventTickets = await fetchAdminEventTickets(eventId);
    }
  }

  async function run(action: () => Promise<unknown>, successNote?: string) {
    busy = true;
    actionError = null;
    notice = null;
    try {
      await action();
      if (successNote) notice = successNote;
      await store?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Action failed.';
    } finally {
      busy = false;
    }
  }

  function releaseExpired() {
    void run(async () => {
      const result = await sweepExpiredReservations(eventId);
      notice = `Released ${result.released} expired hold${result.released === 1 ? '' : 's'}.`;
    });
  }

  function releaseHold(reservationId: string) {
    void run(() => releaseConvexReservation(reservationId, 'released_by_manager'), 'Hold released.');
  }

  function saveCapacity(record: AdminInventorySkuRecord) {
    const capacity = capacityDraft.trim() === '' ? -1 : Number(capacityDraft);
    if (!Number.isFinite(capacity) || (capacity !== -1 && capacity < 0)) {
      actionError = 'Capacity must be empty (unlimited) or a number ≥ 0.';
      return;
    }
    void run(() => updateAdminExtraCapacity(eventId, record.sku, capacity), 'Capacity updated.');
  }

  function saveQuantityFor(record: AdminInventorySkuRecord, entry: { ticketExtraId: string | null }) {
    if (!entry.ticketExtraId) return;
    saveQuantity(record, entry.ticketExtraId);
  }

  function saveQuantity(record: AdminInventorySkuRecord, entryId: string) {
    const draft = quantityDrafts[entryId];
    const quantity = Number(draft);
    if (!Number.isFinite(quantity) || quantity < 0) {
      actionError = 'Quantity must be 0 or more.';
      return;
    }
    void run(() => updateAdminTicketExtraQuantity(entryId, quantity), 'Quantity updated.');
  }

  function addExtraToTicket(record: AdminInventorySkuRecord) {
    if (!addTicketId) return;
    const quantity = Number(addQuantity || '1');
    if (!Number.isFinite(quantity) || quantity < 1) {
      actionError = 'Quantity must be at least 1.';
      return;
    }
    void run(() => addAdminTicketExtra(addTicketId, { sku: record.sku, quantity }), 'Extra added.');
  }

  function availabilityLabel(record: AdminInventorySkuRecord): string {
    if (record.capacity === -1) return 'Unlimited';
    if (record.available <= 0) return 'Sold out';
    if (record.available <= 5) return `${record.available} left — low`;
    return `${record.available} left`;
  }

  function tierDisplayName(record: AdminInventorySkuRecord): string {
    if (record.sku === 'event:capacity') return 'Event capacity (paid heads)';
    return `Tier · ${record.sku.slice('tier:'.length)}`;
  }
</script>

<section class="inventory surface surface--sunken" aria-label={`Inventory for ${eventName}`}>
  <header class="inventory__header">
    <h4>Inventory · {eventName}</h4>
    <button type="button" class="secondary" on:click={releaseExpired} disabled={busy || expiredHoldCount === 0}>
      Release expired holds{expiredHoldCount > 0 ? ` (${expiredHoldCount})` : ''}
    </button>
  </header>

  {#if actionError}
    <Notice variant="error">{actionError}</Notice>
  {/if}
  {#if notice}
    <Notice>{notice}</Notice>
  {/if}

  {#if loading && records.length === 0}
    <LoadingBlock label="Loading inventory…" />
  {:else if error}
    <Notice variant="error">Could not load inventory: {error}</Notice>
  {:else if records.length === 0}
    <p class="inventory__empty">No extras or capacity tracking configured for this event.</p>
  {:else}
    {#if tierRecords.length > 0}
      <div class="inventory__group">
        <h5>Ticket capacity</h5>
        <ul>
          {#each tierRecords as record (record.sku)}
            <li class="inventory__row">
              <div class="inventory__info">
                <span class="inventory__name">{tierDisplayName(record)}</span>
                <span class="inventory__meta">
                  {record.sold} paid · {record.reserved} held · {availabilityLabel(record)}
                </span>
              </div>
            </li>
          {/each}
        </ul>
        <p class="inventory__hint">Tier and event capacity are edited in Settings → Tickets & capacity.</p>
      </div>
    {/if}

    <div class="inventory__group">
      <h5>Extras</h5>
      {#if extraRecords.length === 0}
        <p class="inventory__empty">No extras configured.</p>
      {:else}
        <ul>
          {#each extraRecords as record (record.sku)}
            <li class="inventory__item">
              <button type="button" class="inventory__row inventory__row--button" on:click={() => toggleSku(record)}>
                <div class="inventory__info">
                  <span class="inventory__name">{record.name} <code>{record.sku}</code></span>
                  <span class="inventory__meta">
                    {record.sold} sold · {record.reserved} held · {availabilityLabel(record)}
                  </span>
                </div>
                <span class="inventory__toggle">{openSku === record.sku ? 'Close' : 'Details'}</span>
              </button>

              {#if openSku === record.sku}
                <div class="inventory__detail">
                  <div class="inventory__capacity">
                    <label>
                      Capacity (empty = unlimited)
                      <input type="number" min="0" bind:value={capacityDraft} />
                    </label>
                    <button type="button" class="secondary" disabled={busy} on:click={() => saveCapacity(record)}>
                      Save capacity
                    </button>
                  </div>

                  {#if record.committed.length > 0}
                    <div class="inventory__section">
                      <h6>Purchased ({record.committed.length})</h6>
                      <ul>
                        {#each record.committed as entry (entry.ticketExtraId ?? `${entry.ticketId}-${entry.quantity}`)}
                          <li class="inventory__entry">
                            <span>
                              {entry.personName ?? 'Unknown'}
                              {#if entry.ticketCode}
                                · {entry.ticketCode}
                              {/if}
                            </span>
                            {#if entry.ticketExtraId}
                              <span class="inventory__qty">
                                <input
                                  type="number"
                                  min="0"
                                  placeholder={String(entry.quantity)}
                                  bind:value={quantityDrafts[entry.ticketExtraId]}
                                />
                                <button
                                  type="button"
                                  class="ghost"
                                  disabled={busy || !quantityDrafts[entry.ticketExtraId]}
                                  on:click={() => saveQuantityFor(record, entry)}
                                >
                                  Save
                                </button>
                              </span>
                            {:else}
                              <span class="inventory__meta">×{entry.quantity}</span>
                            {/if}
                          </li>
                        {/each}
                      </ul>
                    </div>
                  {/if}

                  {#if record.holds.length > 0}
                    <div class="inventory__section">
                      <h6>Active holds ({record.holds.length})</h6>
                      <ul>
                        {#each record.holds as hold (hold.reservationId)}
                          <li class="inventory__entry">
                            <span>
                              {hold.personName ?? 'Unknown'} · ×{hold.quantity}
                              {#if hold.expiresAt}
                                · {hold.expiresAt <= Date.now() ? 'expired' : 'expires'}
                                {new Date(hold.expiresAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                              {/if}
                            </span>
                            <button
                              type="button"
                              class="ghost"
                              disabled={busy}
                              on:click={() => releaseHold(hold.reservationId)}
                            >
                              Release
                            </button>
                          </li>
                        {/each}
                      </ul>
                    </div>
                  {/if}

                  <div class="inventory__section">
                    <h6>Add to a ticket</h6>
                    <div class="inventory__add">
                      <select bind:value={addTicketId}>
                        <option value="">Choose guest…</option>
                        {#each eventTickets as ticket (ticket.ticketId)}
                          <option value={ticket.ticketId}>{ticket.personName ?? ticket.code ?? ticket.ticketId}</option>
                        {/each}
                      </select>
                      <input type="number" min="1" bind:value={addQuantity} />
                      <button
                        type="button"
                        class="secondary"
                        disabled={busy || !addTicketId}
                        on:click={() => addExtraToTicket(record)}
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              {/if}
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</section>

<style>
  .inventory {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
  }

  .inventory__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .inventory h4 {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .inventory__group h5 {
    margin: var(--space-2) 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .inventory ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .inventory__item {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .inventory__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
  }

  .inventory__row--button {
    width: 100%;
    cursor: pointer;
    text-align: left;
    color: var(--color-text-primary);
    font: inherit;
  }

  .inventory__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .inventory__name {
    font-weight: 600;
    font-size: var(--font-size-sm);
  }

  .inventory__name code {
    font-size: var(--font-size-2xs);
    color: var(--color-text-muted);
    margin-left: var(--space-2);
  }

  .inventory__meta {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .inventory__toggle {
    font-size: var(--font-size-xs);
    color: var(--color-text-amber);
    flex-shrink: 0;
  }

  .inventory__detail {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
  }

  .inventory__capacity {
    display: flex;
    align-items: flex-end;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .inventory__capacity label {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .inventory__section h6 {
    margin: 0 0 var(--space-2);
    font-size: var(--font-size-2xs);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .inventory__entry {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    font-size: var(--font-size-sm);
    padding: var(--space-1) 0;
    flex-wrap: wrap;
  }

  .inventory__qty {
    display: flex;
    gap: var(--space-2);
    align-items: center;
  }

  .inventory__add {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .inventory__add select {
    flex: 1 1 200px;
  }

  input,
  select {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

  input[type='number'] {
    width: 90px;
  }

  .inventory__hint {
    margin: var(--space-2) 0 0;
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .inventory__empty {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }
</style>
